import { Droppable } from 'react-beautiful-dnd';
import {
  Button,
  Dropdown,
  Group,
  IconArticle,
  IconRefresh,
  IconThreeDotsVer,
  Typography,
  Wrapper
} from '@screentone/core';
import classnames from 'classnames';
import { format } from 'date-fns-tz';

import { CommonEntityProps } from 'components/datamodel/commonEntityProps';
// eslint-disable-next-line import/no-cycle -- TODO: pre-existing cycle via DataModelContext, fix when context is restructured
import { MenuActionsKeys } from 'contexts/context-menu-actions/ContextMenuActionsContext';
// eslint-disable-next-line import/no-cycle -- TODO: pre-existing cycle via DataModelContext, fix when context is restructured
import { useContextMenuActions } from 'contexts/context-menu-actions/useContextMenuActions';
// eslint-disable-next-line import/no-cycle -- TODO: pre-existing cycle via DataModelContext, fix when context is restructured
import { useDataModelContext } from 'contexts/datamodel/useDataModel';
import { DROPPABLE_ID_PREFIXES } from 'contexts/drag-and-drop/dragUtils';
import { OffPlatformListContainer } from 'data/generated/graphql';
import { getNumTotalItemsBeforeIndex } from 'utils/modules';
import styles from './OffPlatform.module.scss';

interface OffPlatformProps extends CommonEntityProps {
  data: OffPlatformListContainer;
  isHistory: boolean;
  isInEditMode?: boolean;
}

const OffPlatform = ({ data, parentHierarchyId, index, isHistory, isInEditMode }: OffPlatformProps) => {
  const { generateHierarchyId, metadata, renderEntity, setNewRoot } = useDataModelContext();
  const { renderActions } = useContextMenuActions();

  const hierarchyId = generateHierarchyId(data, parentHierarchyId, index);

  const numTotalItemsUsed = getNumTotalItemsBeforeIndex(data, data.collection.length);

  return (
    <>
      {!isHistory && (
        <Typography variant="header3" margin={{ bottom: 'md' }} data-testid="off-platform-new-header">
          {isInEditMode ? `Editing: ${format(metadata.publishUtc!, 'MMM d, yyyy h:mm aaa z')}` : 'New'}
        </Typography>
      )}
      <Wrapper margin={{ bottom: 'md' }} data-testid="off-platform-container">
        <div
          data-testid="off-platform-items-scrollable"
          className={classnames(styles.itemsScrollable, {
            [styles.draftItems]: !isHistory
          })}
        >
          <Group margin={{ bottom: 'xs' }} align="space-between">
            <Typography weight="bold" size="sm" margin={{ bottom: 'sm' }} data-testid="off-platform-display-name">
              {data.attributes.moduleDisplayName}
            </Typography>
            {!isHistory && (
              <Dropdown padding={{ all: 'none' }} position="right" trigger={<IconThreeDotsVer color="asphalt" />}>
                {renderActions(MenuActionsKeys.DraftModule, {
                  entity: data,
                  index,
                  extraProperties: { numTotalItemsUsed, maxAllowedItems: data.attributes.maxItems }
                })}
              </Dropdown>
            )}
          </Group>
          <Droppable
            droppableId={(isHistory ? DROPPABLE_ID_PREFIXES.HISTORY : DROPPABLE_ID_PREFIXES.OFF_PLATFORM) + hierarchyId}
            isDropDisabled={isHistory}
          >
            {(droppableProvided) => (
              <div
                className={classnames(styles.itemsList, {
                  [styles.draftItemsList]: !isHistory
                })}
                ref={droppableProvided.innerRef}
                {...droppableProvided.droppableProps}
              >
                {data.collection.map((item, mapIndex) => {
                  const articleHierarchyId = generateHierarchyId(item, hierarchyId, mapIndex);
                  const availableContentSlots = data.attributes.maxItems - getNumTotalItemsBeforeIndex(data, mapIndex);

                  return (
                    <div key={mapIndex}>
                      {renderEntity(item, {
                        hierarchyId,
                        index: mapIndex,
                        isHistory,
                        extraProps: {
                          content: { availableContentSlots, isDraft: !isHistory },
                          article: {
                            renderActions: () =>
                              renderActions(isHistory ? MenuActionsKeys.HistoryArticle : MenuActionsKeys.DraftArticle, {
                                hierarchyId: articleHierarchyId,
                                index: mapIndex,
                                entity: item
                              })
                          }
                        }
                      })}
                    </div>
                  );
                })}
                {droppableProvided.placeholder}
                {numTotalItemsUsed !== data.attributes.maxItems && !isHistory && (
                  <Wrapper
                    className={classnames(styles.itemsNumber, {
                      [styles.itemsNumberWithContent]: numTotalItemsUsed > 0
                    })}
                    padding={{ all: 'md' }}
                  >
                    <IconArticle color="asphalt" margin={{ right: 'xs' }} />
                    <Typography variant="note" data-testid="off-platform-items-count">
                      {numTotalItemsUsed}/{data.attributes.maxItems} articles
                    </Typography>
                  </Wrapper>
                )}
              </div>
            )}
          </Droppable>
        </div>
      </Wrapper>
      {!isHistory && (
        <Group align="end">
          <Button
            data-testid="off-platform-reset-to-default"
            tertiary
            icon={IconRefresh}
            color="lava"
            onClick={() => {
              setNewRoot({ ...data, collection: [] });
            }}
          >
            Reset to default
          </Button>
        </Group>
      )}
    </>
  );
};

export default OffPlatform;
